"use strict";
/*
 * CPM Auth NPM Package
 * Copyright (C) 2023 CPM
 *
 * All rights reserved.
 *
 * This software and its documentation are considered to be the Confidential and Proprietary Information of
 * CPM. Any use, copying, reproduction, modification, distribution, or disclosure directly
 * or indirectly, in whole or in part, including in derivative works, is prohibited without the express written
 * consent of CPM.
 */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.registerRefreshTimeout = exports.getSessionType = exports.refresh = exports.generateTokenHash = exports.InvalidCSRFTokenError = exports.UnknownRefreshError = exports.BadRefreshError = exports.isTokenExpired = exports.getTokenExpiration = exports.getCurrentCSRFToken = exports.getCurrentAccessToken = exports.decodeAccessToken = exports.InvalidAccessTokenError = exports.handleRedirect = exports.UndefinedSSOURLError = exports.REFRESH_LOCK_KEY = void 0;
const js_cookie_1 = __importDefault(require("js-cookie"));
const jwt_decode_1 = __importDefault(require("jwt-decode"));
const crypto = __importStar(require("crypto-js"));
const cookie_lock_1 = require("./cookie-lock");
//  Define Constants
const REFRESH_WINDOW_MS = 60 * 30 * 1000; //  Define Refresh Window (30 Minutes)
const SSO_ACCESS_TOKEN_COOKIE = "cpm-sso-token";
const SSO_CSRF_TOKEN_COOKIE = "cpm-sso-csrf-token";
exports.REFRESH_LOCK_KEY = "CPM_REFRESH_LOCK";
const REFRESH_HEARTBEAT_INTERVAL = 30000;
//
//  Redirect Utils
//
class UndefinedSSOURLError extends Error {
}
exports.UndefinedSSOURLError = UndefinedSSOURLError;
const handleRedirect = (redirect, ssoUrl) => {
    console.warn("Handling Redirect");
    if (redirect) {
        if (!!ssoUrl) {
            console.log("Redirecting to SSO");
            window.location.href = ssoUrl;
        }
        else if (!ssoUrl) {
            console.error("Failed to Redirect:  No SSL URL is defined.");
            throw new UndefinedSSOURLError();
        }
    }
};
exports.handleRedirect = handleRedirect;
//
//  Access Token Utils
//
class InvalidAccessTokenError extends Error {
}
exports.InvalidAccessTokenError = InvalidAccessTokenError;
/**
 * Decodes a CPM Access Token (JWT)
 * @param {string} accessToken - CPM Access Token (JWT)
 * @returns {IDecodedAccessToken} Decoded CPM Access Token
 */
const decodeAccessToken = (accessToken) => {
    let decodedToken;
    try {
        if (!accessToken) {
            console.error("Missing Access Token Error");
            throw new InvalidAccessTokenError();
        }
        decodedToken = (0, jwt_decode_1.default)(accessToken, { payload: true });
        return decodedToken;
    }
    catch (e) {
        console.error("Invalid Access Token Error:  ", e);
        throw new InvalidAccessTokenError();
    }
};
exports.decodeAccessToken = decodeAccessToken;
/**
 * Retrieves the current Access Token Cookie
 * @returns {string | undefined} CPM Access Token (JWT)
 */
const getCurrentAccessToken = () => {
    return js_cookie_1.default.get(SSO_ACCESS_TOKEN_COOKIE);
};
exports.getCurrentAccessToken = getCurrentAccessToken;
/**
 * Retrieves the current CSRF Token Cookie
 * @returns {string | undefined} CPM CSRF Token (JSON)
 */
const getCurrentCSRFToken = () => {
    return js_cookie_1.default.get(SSO_CSRF_TOKEN_COOKIE);
};
exports.getCurrentCSRFToken = getCurrentCSRFToken;
/**
 * Get time remaining before Access Token Cookie expiration.
 * @param {IDecodedAccessToken} Decoded Access Token
 * @returns {number} Expiration Date expressed in milliseconds since the Epoch
 */
const getTokenExpiration = (decodedAccessToken) => {
    const exp = decodedAccessToken["exp"];
    const expTimeMs = exp * 1000; //  NOTE:  exp is expressed in seconds.  Convert to MS.
    return expTimeMs;
};
exports.getTokenExpiration = getTokenExpiration;
/**
 * Checks if a CPM token is expired.
 * @param {string CPM Access Token
 * @returns {boolean} True is Expired, False if not
 */
const isTokenExpired = (token) => {
    const decodedToken = (0, exports.decodeAccessToken)(token);
    const exp = decodedToken["exp"];
    const expTimeMs = exp * 1000; //  NOTE:  exp is expressed in seconds.  Convert to MS.
    const currentTimeMS = new Date().getTime();
    const isExpired = currentTimeMS >= expTimeMs;
    return isExpired;
};
exports.isTokenExpired = isTokenExpired;
//
//  Refresh Logic
//
//  We use a "Refresh Lock" to prevent multiple tabs from refreshing at the same time.
//
//  Define Errors
class UndefinedCSRFTokenError extends Error {
}
class AccessTokenDecodingError extends Error {
}
class BadRefreshError extends Error {
}
exports.BadRefreshError = BadRefreshError;
class UnknownRefreshError extends Error {
}
exports.UnknownRefreshError = UnknownRefreshError;
class InvalidCSRFTokenError extends Error {
}
exports.InvalidCSRFTokenError = InvalidCSRFTokenError;
/**
 * Generates an MD5 TokenHash for the current CPM CSRF Token.
 * @returns {TokenHash} MD5 Hash
 */
const generateTokenHash = () => {
    const token = (0, exports.getCurrentCSRFToken)();
    if (!token) {
        throw new UndefinedCSRFTokenError();
    }
    return crypto.MD5(token).toString();
};
exports.generateTokenHash = generateTokenHash;
/**
 * Calculates the time remaining until the start of the Refresh Window.
 * @description The Refresh Window is the period of time in which the Token Session can be re-authenticated using a Refresh Token.
 *              The "Refresh Window" begins { REFRESH_WINDOW_MS } milliseconds prior to Access Token Expiration.
 * @param {IDecodedAccessToken} Decoded CPM Access Token
 * @returns {number} Milliseconds until the start of the Refresh Window
 */
const timeUntilRefreshWindow = (decodedToken) => {
    const currentTimeMs = new Date().getTime();
    const expTimeMs = (0, exports.getTokenExpiration)(decodedToken);
    const refreshTimeMs = expTimeMs - REFRESH_WINDOW_MS;
    return refreshTimeMs - currentTimeMs;
};
/**
 * Refreshes a user's session with a direct call to SSO.
 */
const refreshDirect = async (ssoUrl) => {
    console.log("Refreshing Session");
    const csrfToken = (0, exports.getCurrentCSRFToken)();
    if (!csrfToken) {
        console.error("No CSRF Token Available");
        throw new InvalidCSRFTokenError();
    }
    const result = await fetch(`${ssoUrl}/api/v1/refresh`, {
        credentials: "include",
        method: "post",
        headers: {
            "X-CSRF-TOKEN": csrfToken,
        },
    });
    if (result.status === 401) {
        throw new BadRefreshError();
    }
    else if (result.status === 200) {
        const json = await result.json();
        const cookie = (0, exports.getCurrentAccessToken)();
        console.log("Refresh Complete", { json, result, cookie, status: result === null || result === void 0 ? void 0 : result.status });
        const accessToken = (0, exports.getCurrentAccessToken)();
        return accessToken;
    }
    else {
        throw new UnknownRefreshError();
    }
};
/**
 * Re-authenticates the user and extends their Login Session using the current Refresh Token.
 *
 * @description Claims the browser-wide "Refresh Lock" if it is not already set OR if
 *
 * @param {string} SSO Url
 * @param {redirect} Redirect URL
 * @param {hold} When True, hold until all refresh attempts complete.  Otherwise, return immediately.
 * @returns {Promise<string | void>} CPM Access Token (JWT)
 */
const refresh = async (ssoUrl, redirect, hold = false) => {
    try {
        await (0, cookie_lock_1.acquireCookieLock)(exports.REFRESH_LOCK_KEY, { hold });
        try {
            const accessToken = await refreshDirect(ssoUrl);
            (0, cookie_lock_1.clearLock)(exports.REFRESH_LOCK_KEY);
            return accessToken;
        }
        catch (err) {
            if (err instanceof BadRefreshError) {
                (0, cookie_lock_1.clearLock)(exports.REFRESH_LOCK_KEY);
                console.error("Bad Refresh Error");
                if (redirect) {
                    window.location.href = ssoUrl;
                }
            }
            else {
                console.error(`Unknown Refresh Error`);
                (0, cookie_lock_1.clearLock)(exports.REFRESH_LOCK_KEY);
            }
        }
    }
    catch (err) {
        if (err instanceof cookie_lock_1.LockTakenError) {
            console.log("Failed to Acquire Refresh Lock:  A Refresh is already in progress on another tab.");
        }
        else {
            console.log("Failed to Acquire Refresh Lock - An unknown error occurred while attempting to acquire the refresh lock.");
        }
    }
};
exports.refresh = refresh;
/**
 * Determines the Session Type of the current login session.
 *
 * NOTE:  A CSRF Token is *always* present and mandatory for a "Refresh" session.
 * Therefore, we use this as an indicator for the session type.
 *
 * @returns
 */
const getSessionType = () => {
    const isRefresh = !!(0, exports.getCurrentCSRFToken)();
    return isRefresh ? "refresh" : "basic";
};
exports.getSessionType = getSessionType;
/**
 * Registers a 'Refresh Heartbeat' (JS Timeout) to regularly refresh the access token prior to expiration.
 *
 * There are 4 cases (Session:  Basic, Refresh) (Access Token:  Invalid, Valid)
 *   Invalid Access Token (Includes Missing Tokens)
 *     -  Basic Session -> Redirect (if configured)
 *     -  Refresh Session -> Refresh & Register
 *   Valid Access Token
 *     -  Basic Session -> NOP
 *     -  Refresh Session -> Register
 *
 * Notes:
 *  -  If your app makes authenticated asynchronous requests on load, then you will need use the Axios Interceptor.
 *     This prevents a race between the refresh and your request.
 *  -  If the script context loses focus, it will trigger on focus and re-check the token.
 *
 * @param {IOptions} options for the timeout registration
 */
const registerRefreshTimeout = async ({ redirect, ssoUrl, skipInitial, callback }) => {
    console.log("Refresh Heartbeat");
    let decodedToken;
    //  Determine Session Type
    const sessionType = (0, exports.getSessionType)();
    try {
        //  Get the Access Token
        const currentAccessToken = (0, exports.getCurrentAccessToken)();
        //  Parse the Access Token
        decodedToken = (0, exports.decodeAccessToken)(currentAccessToken);
    }
    catch (err) {
        //  Handle Invalid Token + Basic Session
        if (sessionType === "basic") {
            console.warn("Basic Session Detected with an Invalid Access Token");
            (0, exports.handleRedirect)(redirect, ssoUrl);
            return;
            //  Handle Invalid Token + Refresh Session
        }
        else {
            console.log("Refresh Session Detected with an Invalid Access Token.  Attempting refresh...");
            await (0, exports.refresh)(ssoUrl, redirect);
            if (callback)
                callback();
            //  Register Next Heartbeat
            setTimeout(() => (0, exports.registerRefreshTimeout)({ redirect, ssoUrl, callback, skipInitial: false }), REFRESH_HEARTBEAT_INTERVAL);
            return;
        }
    }
    //  Handle Valid Access Token (Basic Session)
    if (sessionType === "basic") {
        console.log("Basic Session:  Not registering refresh timeout for non refresh enabled user. (no csrf token)");
        return;
    }
    //  Handle Valid Access Token (Refresh Session)
    const timeUntilRefresh = timeUntilRefreshWindow(decodedToken);
    const shouldRefresh = !skipInitial && (timeUntilRefresh <= 0);
    if (shouldRefresh) {
        await (0, exports.refresh)(ssoUrl, redirect);
        if (callback)
            callback();
    }
    //  Register Next Heartbeat
    setTimeout(() => (0, exports.registerRefreshTimeout)({ redirect, ssoUrl, callback, skipInitial: false }), REFRESH_HEARTBEAT_INTERVAL);
};
exports.registerRefreshTimeout = registerRefreshTimeout;
